/**
 * 66.7 F2 (lane F; ARCHITECTURE §5.15): the fine ground as a table — the cover system's relief.
 *
 * A person's ground (`personSurfaceRadius`: the mesh and the fine relief) is read many times along every line a cover
 * question asks (a body against a threat, a spot's horizon toward it), and each read is a mesh lookup and two octaves of
 * value noise. Here it is kept as a grid: §5.3's cube-sphere cells at about a person across, each holding the exact ground
 * at its centre, kept a page (32 × 32 cells) at a time and filled lazily a cell at a time (69.67: a far ray read a page's
 * every cell for the few it crossed — 56 ms of one tick); between centres the ground is read bilinearly (within a
 * cube face; at a face's edge, the nearest centre). A crater drops only the pages near the vertices it moved (the mesh's
 * facets round them); nothing else is ever stale, so a value never depends on who asked first.
 *
 * The grid is an approximation of the ground between its centres: its readers ask with a margin and answer "unsure" when
 * a line passes within it, and the caller then asks the exact rays (core/coverMap.ts `shelter`). Not state: nothing here
 * enters the match.
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { cellCentre } from './cells';
import { dacos, dcos, dtan } from './dmath';
import { personSurfaceRadius } from './fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';

export const RELIEF = {
  /** A cell's size at a face's centre (people). */
  cellPeople: 1,
  /** A page's cells per edge. */
  page: 32,
  /** How far apart a line's samples are taken (people): a person apart, against a margin of a tenth of one, agreed with the exact line in 8,000 random lines round the 50-a-side battle (lab-7), none wrong. */
  stepPeople: 1,
} as const;

/** Work done (counted, not timed; nothing in the game reads it): grid reads of the ground, pages begun, and cells filled (each an exact lookup). */
export const reliefWork = { reads: 0, pages: 0, cells: 0 };
export function resetReliefWork(): void { reliefWork.reads = 0; reliefWork.pages = 0; reliefWork.cells = 0; }

/** What a person's ground reads beside the mesh: a page's cells are the ground as it stood when the page was begun, whenever each is filled. */
type GroundEdits = Pick<PlanetState, 'digs' | 'craterlets' | 'terraces' | 'stands' | 'standCells'>;
interface Page {
  /** The ground at each cell's centre, less R (NaN past the face's edge), where `got`. */
  v: Float32Array; got: Uint8Array;
  face: number; i0: number; j0: number;
  edits: GroundEdits;
}

interface Relief {
  n: number; R: number; P: number; person: number;
  basePositions: Float32Array; heightmap: Float32Array;
  /** Per page (face, page row, page column; an index into a sparse array, no hashing). */
  pages: Array<Page | undefined>;
  /** Each page's centre direction, for dropping pages near moved ground. */
  centres: Map<number, Vec3>;
  lastId: number; last: Page | null;
  /** The planet's edits as last seen (shared by the pages begun under them), and the planet each page's edits are read on. */
  edits: GroundEdits | null; viewsOf: PlanetState | null; views: Map<GroundEdits, PlanetState>;
}

/** Per config (a match's), per planet index. */
let reliefs = new WeakMap<object, Map<number, Relief>>();
/** For the tests: run `fn` with no relief kept, then put back what was. */
export function reliefFromNothing<T>(fn: () => T): T {
  const saved = reliefs;
  reliefs = new WeakMap();
  try { return fn(); } finally { reliefs = saved; }
}

/** Per mesh: the longest facet edge as an angle — how far a moved vertex's ground reaches. */
const edgeAngles = new WeakMap<Float32Array, number>();
function longestEdgeAngle(bp: Float32Array): number {
  const kept = edgeAngles.get(bp);
  if (kept !== undefined) return kept;
  let minCos = 1;
  const cosAt = (i: number, j: number) => {
    const ix = bp[i * 3], iy = bp[i * 3 + 1], iz = bp[i * 3 + 2], jx = bp[j * 3], jy = bp[j * 3 + 1], jz = bp[j * 3 + 2];
    return (ix * jx + iy * jy + iz * jz) / (Math.sqrt(ix * ix + iy * iy + iz * iz) * Math.sqrt(jx * jx + jy * jy + jz * jz) || 1);
  };
  for (let t = 0; t + 2 < bp.length / 3; t += 3) minCos = Math.min(minCos, cosAt(t, t + 1), cosAt(t + 1, t + 2), cosAt(t, t + 2));
  const a = dacos(Math.max(-1, Math.min(1, minCos)));
  edgeAngles.set(bp, a);
  return a;
}

function reliefOf(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number): Relief | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state);
  const person = PERSON_BODY_HEIGHT * ps.k;
  let byPlanet = reliefs.get(state.config);
  if (!byPlanet) { byPlanet = new Map(); reliefs.set(state.config, byPlanet); }
  const n = Math.max(8, Math.ceil((2 * planet.radius) / (RELIEF.cellPeople * person)));
  let r = byPlanet.get(planetIndex);
  if (!r || r.n !== n || r.R !== planet.radius || r.basePositions !== planet.basePositions || r.heightmap.length !== planet.heightmap.length) {
    r = { n, R: planet.radius, P: Math.ceil(n / RELIEF.page), person, basePositions: planet.basePositions, heightmap: planet.heightmap, pages: [], centres: new Map(), lastId: -1, last: null, edits: null, viewsOf: null, views: new Map() };
    byPlanet.set(planetIndex, r);
  } else if (r.heightmap !== planet.heightmap) {
    dropMoved(r, planet);
    r.heightmap = planet.heightmap;
  }
  return r;
}

/** A crater (a new heightmap): drop the pages within reach of a moved vertex — its facets, and the page's own half-width. */
function dropMoved(r: Relief, planet: PlanetState): void {
  if (r.centres.size === 0) return;
  const before = r.heightmap, h = planet.heightmap, bp = planet.basePositions;
  const pageAngle = (Math.SQRT2 * RELIEF.page * 2) / r.n;   // a page's half-diagonal, generously (gnomonic cells are smallest mid-face)
  const reach = 2 * longestEdgeAngle(bp) + pageAngle;
  const cosReach = dcos(Math.min(Math.PI, reach)) - 1e-12;
  const moved: Vec3[] = [];
  for (let v = 0; v < h.length; v++) {
    if (h[v] === before[v]) continue;
    const x = bp[v * 3], y = bp[v * 3 + 1], z = bp[v * 3 + 2], l = Math.sqrt(x * x + y * y + z * z) || 1;
    moved.push({ x: x / l, y: y / l, z: z / l });
  }
  if (moved.length === 0) return;
  for (const [id, c] of r.centres) {
    if (moved.some(m => m.x * c.x + m.y * c.y + m.z * c.z >= cosReach)) { r.pages[id] = undefined; r.centres.delete(id); }
  }
  r.lastId = -1; r.last = null;
}

function editsOf(r: Relief, planet: PlanetState): GroundEdits {
  const e = r.edits;
  if (e && e.digs === planet.digs && e.craterlets === planet.craterlets && e.terraces === planet.terraces && e.stands === planet.stands && e.standCells === planet.standCells) return e;
  return (r.edits = { digs: planet.digs, craterlets: planet.craterlets, terraces: planet.terraces, stands: planet.stands, standCells: planet.standCells });
}

function pageOf(r: Relief, planet: PlanetState, face: number, i: number, j: number): Page {
  const pr = Math.floor(i / RELIEF.page), pc = Math.floor(j / RELIEF.page);
  const id = (face * r.P + pr) * r.P + pc;
  if (id === r.lastId && r.last) return r.last;
  let pg = r.pages[id];
  if (!pg) {
    reliefWork.pages++;
    pg = { v: new Float32Array(RELIEF.page * RELIEF.page), got: new Uint8Array(RELIEF.page * RELIEF.page), face, i0: pr * RELIEF.page, j0: pc * RELIEF.page, edits: editsOf(r, planet) };
    r.pages[id] = pg;
    r.centres.set(id, cellCentre((face * r.n + Math.min(r.n - 1, pr * RELIEF.page + RELIEF.page / 2)) * r.n + Math.min(r.n - 1, pc * RELIEF.page + RELIEF.page / 2), r.n));
  }
  r.lastId = id; r.last = pg;
  return pg;
}

/** Cell `k` of page `pg`: filled on its first read, on the planet as it stood when the page was begun (its edits; the mesh a crater left it — a page within its reach is dropped). */
function cellAt(r: Relief, planet: PlanetState, ps: PersonSet, pg: Page, k: number): number {
  if (pg.got[k] === 0) {
    reliefWork.cells++;
    const ci = pg.i0 + Math.floor(k / RELIEF.page), cj = pg.j0 + (k % RELIEF.page);
    if (ci < r.n && cj < r.n) {
      if (r.viewsOf !== planet) { r.viewsOf = planet; r.views.clear(); }
      let on = r.views.get(pg.edits);
      if (!on) { on = editsOf(r, planet) === pg.edits ? planet : { ...planet, ...pg.edits }; r.views.set(pg.edits, on); }
      pg.v[k] = personSurfaceRadius(on, cellCentre((pg.face * r.n + ci) * r.n + cj, r.n), ps) - r.R;
    } else pg.v[k] = NaN;
    pg.got[k] = 1;
  }
  return pg.v[k];
}

function cellValue(r: Relief, planet: PlanetState, ps: PersonSet, face: number, i: number, j: number): number {
  return cellAt(r, planet, ps, pageOf(r, planet, face, i, j), (i % RELIEF.page) * RELIEF.page + (j % RELIEF.page));
}

/** The grid's ground radius in direction (x, y, z) (need not be unit): bilinear between the cell centres round it on its face. */
function groundWith(r: Relief, planet: PlanetState, ps: PersonSet, x: number, y: number, z: number): number {
  reliefWork.reads++;
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  let face: number, u: number, v: number;
  if (ax >= ay && ax >= az) { face = x >= 0 ? 0 : 1; u = y / ax; v = z / ax; }
  else if (ay >= az) { face = y >= 0 ? 2 : 3; u = x / ay; v = z / ay; }
  else { face = z >= 0 ? 4 : 5; u = x / az; v = y / az; }
  const n = r.n;
  const fi = (u + 1) * 0.5 * n - 0.5, fj = (v + 1) * 0.5 * n - 0.5;
  let i0 = Math.floor(fi), j0 = Math.floor(fj), tx = fi - i0, ty = fj - j0;
  if (i0 < 0) { i0 = 0; tx = 0; } else if (i0 >= n - 1) { i0 = n - 2; tx = 1; }
  if (j0 < 0) { j0 = 0; ty = 0; } else if (j0 >= n - 1) { j0 = n - 2; ty = 1; }
  let h00: number, h10: number, h01: number, h11: number;
  const P = RELIEF.page, ii = i0 % P, jj = j0 % P;
  if (ii < P - 1 && jj < P - 1) {
    // (the four centres in one page — 31 times in 32)
    const pg = pageOf(r, planet, face, i0, j0), k = ii * P + jj;
    h00 = cellAt(r, planet, ps, pg, k); h10 = cellAt(r, planet, ps, pg, k + P); h01 = cellAt(r, planet, ps, pg, k + 1); h11 = cellAt(r, planet, ps, pg, k + P + 1);
  } else {
    h00 = cellValue(r, planet, ps, face, i0, j0); h10 = cellValue(r, planet, ps, face, i0 + 1, j0);
    h01 = cellValue(r, planet, ps, face, i0, j0 + 1); h11 = cellValue(r, planet, ps, face, i0 + 1, j0 + 1);
  }
  return r.R + (h00 * (1 - tx) + h10 * tx) * (1 - ty) + (h01 * (1 - tx) + h11 * tx) * ty;
}

/**
 * The relief cells on an even lattice (every `stride`-th cell of each face, counted from the face's corner, so a cell's place
 * never moves) whose centres lie within `angle` radians of `dir` — across face edges too. Each cell once, by face then row.
 */
export function reliefCellsNear(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, dir: Vec3, angle: number, stride: number): Vec3[] {
  const r = reliefOf(state, planetIndex);
  if (!r) return [];
  const d = dir, dl = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z) || 1;
  const ux = d.x / dl, uy = d.y / dl, uz = d.z / dl;
  const cosA = dcos(Math.min(Math.PI, angle)) - 1e-12;
  const out: Vec3[] = [];
  const n = r.n;
  for (let face = 0; face < 6; face++) {
    // the face's axis and its plane coordinates (cells.ts cellOf's layout)
    const sgn = face % 2 === 0 ? 1 : -1, axis = face >> 1;
    const a = (axis === 0 ? ux : axis === 1 ? uy : uz) * sgn;
    if (!(a > 0)) continue;
    const p = axis === 0 ? uy : ux, q = axis === 2 ? uy : uz;
    const u0 = p / a, v0 = q / a;
    // a face the cap cannot reach: its square's nearest point to the direction is further than the cap
    const uc = Math.max(-1, Math.min(1, u0)), vc = Math.max(-1, Math.min(1, v0)), cl = Math.sqrt(1 + uc * uc + vc * vc);
    const cx = axis === 0 ? sgn : uc, cy = axis === 1 ? sgn : axis === 0 ? uc : vc, cz = axis === 2 ? sgn : vc;
    if ((cx * ux + cy * uy + cz * uz) / cl < cosA - 1e-9) continue;
    // the cap's box on this plane round that nearest point: gnomonic stretch there is at most (1 + u² + v²) ≤ 3; widened by a half
    const spread = dtan(Math.min(1.4, angle)) * (1 + uc * uc + vc * vc) * 1.5 + 2 / n;
    const i0 = Math.max(0, Math.floor(((u0 - spread + 1) / 2) * n)), i1 = Math.min(n - 1, Math.ceil(((u0 + spread + 1) / 2) * n));
    const j0 = Math.max(0, Math.floor(((v0 - spread + 1) / 2) * n)), j1 = Math.min(n - 1, Math.ceil(((v0 + spread + 1) / 2) * n));
    for (let i = i0 + ((stride - (i0 % stride)) % stride); i <= i1; i += stride) {
      for (let j = j0 + ((stride - (j0 % stride)) % stride); j <= j1; j += stride) {
        const c = cellCentre((face * n + i) * n + j, n);
        if (c.x * ux + c.y * uy + c.z * uz >= cosA) out.push(c);
      }
    }
  }
  return out;
}

/** A reader of the grid's ground on planet `planetIndex` (radius from its centre, at a direction): set up once for many reads. */
export function reliefReader(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number): (dir: Vec3) => number {
  const r = reliefOf(state, planetIndex), planet = state.planets[planetIndex];
  if (!r) { const R = planet ? planet.radius : 0; return () => R; }
  const ps = personSetOf(state);
  return (d: Vec3) => groundWith(r, planet, ps, d.x, d.y, d.z);
}

/** `reliefReader` taking a direction's components (need not be unit): for hot loops that keep no vectors. */
export function reliefReaderXYZ(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number): (x: number, y: number, z: number) => number {
  const r = reliefOf(state, planetIndex), planet = state.planets[planetIndex];
  if (!r) { const R = planet ? planet.radius : 0; return () => R; }
  const ps = personSetOf(state);
  return (x: number, y: number, z: number) => groundWith(r, planet, ps, x, y, z);
}

/** The grid's reading of a person's ground at `dir` on planet `planetIndex` (radius from its centre). */
export function reliefGround(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, dir: Vec3): number {
  const r = reliefOf(state, planetIndex), planet = state.planets[planetIndex];
  if (!r) return planet ? planet.radius : 0;
  return groundWith(r, planet, personSetOf(state), dir.x, dir.y, dir.z);
}

export type GridLine = 'clear' | 'blocked' | 'unsure';
/**
 * The ground between `a` and `b` (world points near planet `planetIndex`), read off the grid every RELIEF.stepPeople:
 * 'blocked' when a sample lies under it by more than `margin` (world units), 'clear' when every sample stands over it by
 * more than `margin`, else 'unsure' (ask the exact ray). Its ends are not sampled (a body stands on the ground).
 */
export function gridLine(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, a: Vec3, b: Vec3, margin: number): GridLine {
  const r = reliefOf(state, planetIndex), planet = state.planets[planetIndex];
  if (!r) return 'unsure';
  const ps = personSetOf(state);
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z, len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const m = Math.max(1, Math.ceil(len / (RELIEF.stepPeople * r.person)));
  const cx = planet.position.x, cy = planet.position.y, cz = planet.position.z;
  let unsure = false;
  for (let k = 1; k < m; k++) {
    const t = k / m, px = a.x + dx * t - cx, py = a.y + dy * t - cy, pz = a.z + dz * t - cz;
    const dist = Math.sqrt(px * px + py * py + pz * pz);
    const g = groundWith(r, planet, ps, px, py, pz);
    if (dist < g - margin) return 'blocked';
    if (dist <= g + margin) unsure = true;
  }
  return unsure ? 'unsure' : 'clear';
}
